import {User} from "@lib/User/UserModels";
import {USER_COUNTRY_KEY} from "@commons/Models";
import {requiredEnv} from "@msal/msalConfig";
import {useAppContextStore} from "@store/AppContextStore";
import {BREAKPOINT, BreakpointKey, CmsImage} from "@lib/Cms/CmsModels";
import {Lang, readLocale, readLocaleCurrency} from "@i18n/language";

export function sleep(ms: number) {
    return new Promise((resolve) => setTimeout(resolve, ms));
}

export function getCountryFromUser(user: User): string | null {
    const attr = user?.customAttributes?.find((a: any) => a.hurlId === USER_COUNTRY_KEY);
    const v = attr?.values?.[0];
    return v ? String(v).toUpperCase() : null;
}

const assetsPropsCacheMap = new Map<"cdn" | "projectLogicId" | "tenantLogicId", string>();
const assetsCacheMap = new Map<string, string>();

export function publicAssetFullPath(p?: string | null): string {
    if (!p) return "";

    const assetPath = p.trim();

    if (assetPath.startsWith("//") || assetPath.startsWith("/") || assetPath.startsWith("http") || assetPath.startsWith("<svg")) {
        return assetPath;
    }

    const cdnBaseUrl = requiredEnv("VITE_CDN_URL", import.meta.env.VITE_CDN_URL);

    return cdnBaseUrl + (assetPath.startsWith("/") ? assetPath : "/" + assetPath);
}

export function assetFullPath(p: string): string {

    if (!p) return "";

    const assetPath = p.trim();

    if (assetPath.startsWith("//") || assetPath.startsWith("/") || assetPath.startsWith("http") || assetPath.startsWith("<svg")) {
        return assetPath;
    }

    const cachedAsset = assetsCacheMap.get(assetPath);

    if (!!cachedAsset) {
        return cachedAsset;
    }

    if (assetsPropsCacheMap.size === 0) {
        const cdnBaseUrl = requiredEnv("VITE_CDN_URL", import.meta.env.VITE_CDN_URL);
        const {projectId, tenantId} = useAppContextStore.getState();

        if (projectId && tenantId) {
            assetsPropsCacheMap.set("projectLogicId", projectId);
            assetsPropsCacheMap.set("tenantLogicId", tenantId);
        }

        assetsPropsCacheMap.set("cdn", cdnBaseUrl);
    }

    const assetPathPieces = assetPath.split("/");
    const assetFileName = assetPathPieces[assetPathPieces.length - 1];

    const fullPathPieces = [
        assetsPropsCacheMap.get("cdn"),
    ];

    if (!assetFileName.startsWith("shared")) {

        fullPathPieces.push(assetsPropsCacheMap.get("tenantLogicId"));

        if (!assetFileName.startsWith("tenant")) {

            fullPathPieces.push(assetsPropsCacheMap.get("projectLogicId"));

        }

    }

    fullPathPieces.push("assets");
    fullPathPieces.push(...assetPathPieces);

    const path = fullPathPieces.join("/").replace("assets//", "assets/");

    assetsCacheMap.set(assetPath, path)

    return path;
}

export function buildResponsiveImageArray(images?: CmsImage[] | null): string[] {

    if (!images || images.length === 0) return [];

    const byBp: Partial<Record<BreakpointKey, string>> = {};
    let base: string | undefined;

    for (const img of images) {
        const src = img?.src ?? undefined;
        if (!src) continue;

        const bp = img.breakpoint as BreakpointKey | null | undefined;

        if (!bp) {
            if (!base) base = src;
            continue;
        }

        if (!byBp[bp]) byBp[bp] = src;
    }

    const firstAvailable =
        base ??
        byBp[BREAKPOINT.MOBILE] ??
        byBp[BREAKPOINT.TABLET] ??
        byBp[BREAKPOINT.DESKTOP] ??
        byBp[BREAKPOINT.MAX];

    if (!firstAvailable) return [];

    const md = byBp[BREAKPOINT.MOBILE] ?? base ?? firstAvailable;
    const lg = byBp[BREAKPOINT.TABLET] ?? md;
    const xl = byBp[BREAKPOINT.DESKTOP] ?? lg;
    const xxl = byBp[BREAKPOINT.MAX] ?? xl;

    const result = [md, lg, xl, xxl];

    while (result.length > 1 && result[result.length - 1] === result[result.length - 2]) {
        result.pop();
    }

    return result;
}

export function extractLabelFromPipe(value: string | null | undefined): string | null {
    if (!value) return null;

    const strValue = String(value);
    const pipeIndex = strValue.indexOf('|');

    if (pipeIndex === -1) {
        return null;
    }

    let content = strValue.substring(pipeIndex + 1).trim();

    const separatorIndex = content.indexOf(' - ');

    if (separatorIndex !== -1) {
        content = content.substring(0, separatorIndex).trim();
    }

    return content;
}

/**
 * Rimuove dalla stringa lo spazio e il contenuto tra parentesi tonde.
 * Es: "Name (Nome)" -> "Name"
 */
export function removeParenthesesContent(value: string | null | undefined): string {
    if (!value) return "";
    return value.replace(/\s*\([^)]*\)/g, "").trim();
}

export function formatDate(
    value: Date | string | null | undefined,
    {
        preset = "medium",
        withTime = false,
        customOptions,
        defaultValue = "-"
    }: {
        preset?: Intl.DateTimeFormatOptions["dateStyle"];
        withTime?: boolean;
        customOptions?: Intl.DateTimeFormatOptions;
        defaultValue?: string;
    } = {}
): string {

    if (!value) return defaultValue;

    const date = typeof value === "string" ? new Date(value) : value;

    if (isNaN(date.getTime())) return defaultValue;

    const baseOptions: Intl.DateTimeFormatOptions = {
        dateStyle: preset,
    };

    if (withTime) {
        baseOptions.timeStyle = preset;
    }

    const options: Intl.DateTimeFormatOptions = {
        ...baseOptions,
        ...customOptions,
    };

    return new Intl.DateTimeFormat(readLocaleCurrency(), options).format(date);
}

export function formatMoney(
    value: number | null | undefined,
    {
        locale,
        language,
        country2,
        currency,
        currencyDisplay = "symbol",
        minimumFractionDigits,
        maximumFractionDigits,
        defaultValue = "0",
    }: {
        locale?: string;
        language?: Lang;
        country2?: string;
        currency?: string;
        currencyDisplay?: Intl.NumberFormatOptionsCurrencyDisplay;
        minimumFractionDigits?: number;
        maximumFractionDigits?: number;
        defaultValue?: string;
    } = {}
): string {

    if (value === null || value === undefined) return defaultValue;

    const resolvedLocale = locale ?? readLocale(language, country2);
    const {currencyName}= useAppContextStore.getState();

    const options: Intl.NumberFormatOptions = {
        style: "currency",
        currency: currencyName ?? currency ?? "EUR",
        currencyDisplay,
    };

    if (minimumFractionDigits !== undefined) options.minimumFractionDigits = minimumFractionDigits;
    if (maximumFractionDigits !== undefined) options.maximumFractionDigits = maximumFractionDigits;

    return new Intl.NumberFormat(resolvedLocale, options).format(value);
}

export function formatPoints(value: number | null | undefined, defaultValue: string = "0"): string {
    if (!value) return defaultValue;
    return new Intl.NumberFormat(readLocaleCurrency(), {maximumFractionDigits: 0}).format(value);
}

export function floorToStep(n: number, step: number) {
    return n <= 0 ? n : Math.floor(n / step) * step;
}

export function ceilToStep(n: number, step: number) {
    return n <= 0 ? n :Math.ceil(n / step) * step;
}

const DEFAULT_ALPHABET =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";

export function randomString(
    length = 16,
    alphabet: string = DEFAULT_ALPHABET
): string {
    if (length <= 0) return "";

    const cryptoObj = globalThis.crypto;
    if (!cryptoObj?.getRandomValues) {
        throw new Error("Secure random generator not available");
    }

    const result: string[] = [];
    const bytes = new Uint32Array(length);
    cryptoObj.getRandomValues(bytes);

    const alphabetLength = alphabet.length;

    for (let i = 0; i < length; i++) {
        result.push(alphabet[bytes[i] % alphabetLength]);
    }

    return result.join("");
}

export function toSearchParams(record: Record<string, any>): URLSearchParams {
    const params = new URLSearchParams();

    for (const [key, raw] of Object.entries(record)) {
        if (raw === undefined || raw === null) continue;

        if (Array.isArray(raw)) {
            for (const v of raw) {
                if (v === undefined || v === null) continue;
                params.append(key, String(v));
            }
            continue;
        }

        params.append(key, String(raw));
    }

    return params;
}